import {gql} from "@apollo/client";
import {mutate, query} from "../lib/apollo";
import downloadCsv from "../lib/downloadCsv";

const CAMPAIGN_FIELDS = `
    id
    name
    company { id name }
    broker { id name }
    hp_campaign_id
    hp_campaign_id_pr
    active
    url
    categories
    publisherIds
    publishersAllowed
    impressions
    revenue
    revenueMonthly
    dailyCap
    monthlyCap
    rpm
    epc
    epcSource
    manualEpc
    managed
    cfActive
    prActive
    previewUrl
    zips
    zipsAllowed
    programIds
    eduLevels
    degreeLevels
    whenStarts
    trafficTypesBlocked
    hsMin
    hsMax
    militaryOnly
    blockAlreadyEnrolled
    zeroDailyEom
    defaultRpm
`;

export type ClickFeedCampaignLookups = {
    companies: {id: number; value: string}[];
    publishers: {id: number; name: string}[];
    trafficTypes: string[];
    programs: {id: number; label: string; subject: string}[];
};

export async function searchClickFeedCampaigns(input: {
    skip?: number;
    limit?: number;
    search?: string;
    startDate: string;
    endDate: string;
    companyIds?: number[];
    active?: boolean | null;
}) {
    const q = gql`
        query q($input: ClickFeedCampaignSearchCriteria!) {
            clickFeedCampaigns(input: $input) {
                count
                items {
                    ${CAMPAIGN_FIELDS}
                }
            }
        }
    `;

    const response = await query(q, {
        input: {
            skip: input.skip ?? 0,
            limit: input.limit ?? 10000,
            search: input.search || undefined,
            startDate: input.startDate,
            endDate: input.endDate,
            companyIds: input.companyIds?.length ? input.companyIds : undefined,
            active: input.active === null || input.active === undefined ? undefined : input.active,
        },
    });

    if (!response?.clickFeedCampaigns) {
        return {count: 0, items: []};
    }

    return response.clickFeedCampaigns;
}

/** Initial page load: one GraphQL round-trip for campaigns (+ edit lookups only when allowed). */
export async function loadClickFeedCampaignsPage(opts: {
    includeCompanies: boolean;
    includeEditLookups: boolean;
    campaigns: {
        search?: string;
        startDate: string;
        endDate: string;
        companyIds?: number[];
        active?: boolean | null;
        limit?: number;
    };
}): Promise<{
    lookups: ClickFeedCampaignLookups;
    campaigns: {count: number; items: any[]};
}> {
    const emptyLookups: ClickFeedCampaignLookups = {
        companies: [],
        publishers: [],
        trafficTypes: [],
        programs: [],
    };

    const campaignInput = {
        skip: 0,
        limit: opts.campaigns.limit ?? 10000,
        search: opts.campaigns.search || undefined,
        startDate: opts.campaigns.startDate,
        endDate: opts.campaigns.endDate,
        companyIds: opts.campaigns.companyIds?.length ? opts.campaigns.companyIds : undefined,
        active: opts.campaigns.active === null || opts.campaigns.active === undefined
            ? undefined
            : opts.campaigns.active,
    };

    if (!opts.includeEditLookups && !opts.includeCompanies) {
        const response = await query(gql`
            query q($campaignsInput: ClickFeedCampaignSearchCriteria!) {
                clickFeedCampaigns(input: $campaignsInput) {
                    count
                    items {
                        ${CAMPAIGN_FIELDS}
                    }
                }
            }
        `, {campaignsInput: campaignInput});

        return {
            lookups: emptyLookups,
            campaigns: response?.clickFeedCampaigns || {count: 0, items: []},
        };
    }

    const response = await query(gql`
        query q(
            $campaignsInput: ClickFeedCampaignSearchCriteria!
            $publishersInput: PublishersInput!
            $includeCompanies: Boolean!
            $includeEditLookups: Boolean!
        ) {
            clickFeedCampaigns(input: $campaignsInput) {
                count
                items {
                    ${CAMPAIGN_FIELDS}
                }
            }
            companies @include(if: $includeCompanies) {
                id
                active
                name
            }
            publishers(input: $publishersInput) @include(if: $includeEditLookups) {
                items {
                    id
                    name
                }
            }
            publisherTrafficTypes @include(if: $includeEditLookups)
            hpProgramsCatalog @include(if: $includeEditLookups) {
                id
                label
                subject
            }
        }
    `, {
        campaignsInput: campaignInput,
        publishersInput: {
            active: true,
            limit: 10000,
            skip: 0,
            search: "",
            sort: [{column: "name", isAsc: true}],
        },
        includeCompanies: opts.includeCompanies,
        includeEditLookups: opts.includeEditLookups,
    });

    return {
        lookups: {
            companies: (response?.companies || [])
                .filter((c: any) => c.active)
                .map((c: any) => ({id: c.id, value: c.name})),
            publishers: (response?.publishers?.items || []).map((p: any) => ({
                id: p.id,
                name: `${p.name} (${p.id})`,
            })),
            trafficTypes: response?.publisherTrafficTypes || [],
            programs: response?.hpProgramsCatalog || [],
        },
        campaigns: response?.clickFeedCampaigns || {count: 0, items: []},
    };
}

export async function upsertClickFeedCampaign(input: any) {
    const q = gql`
        mutation m($input: ClickFeedCampaignUpsertInput!) {
            clickFeedCampaignUpsert(input: $input) {
                success
                errors
            }
        }
    `;
    const response = await mutate(q, {input});
    return response?.clickFeedCampaignUpsert || {success: false, errors: ["Unknown error"]};
}

export async function setClickFeedCampaignActive(id: number, isActive: boolean) {
    const q = gql`
        mutation m($id: Int!, $isActive: Boolean!) {
            clickFeedCampaignSetActive(id: $id, isActive: $isActive)
        }
    `;
    const response = await mutate(q, {id, isActive});
    return !!response?.clickFeedCampaignSetActive;
}

export function downloadClickFeedCampaignsCsv(rows: any[]) {
    const header = [
        "Id", "Company", "Broker", "App Id", "Url", "CF Id", "PR Id", "Categories", "Publishers",
        "Active", "Impressions", "Revenue", "Daily Cap", "Revenue Monthly", "Monthly Cap", "RPM", "EPC", "EPC Source",
    ];
    const body = rows.map((r) => [
        r.id,
        r.company?.name || "",
        r.broker?.name || "",
        r.name || "",
        r.url || "",
        r.hp_campaign_id,
        r.hp_campaign_id_pr,
        (r.categories || []).join("|"),
        (r.publisherIds || []).join("|"),
        r.active ? "1" : "0",
        r.impressions || 0,
        r.revenue || 0,
        r.dailyCap || 0,
        r.revenueMonthly || 0,
        r.monthlyCap || 0,
        r.rpm || 0,
        r.epc || 0,
        r.epcSource || (r.manualEpc ? "manual" : ""),
    ]);
    downloadCsv("clickfeed_campaigns.csv", [header, ...body]);
}

export const CF_CATEGORIES = [
    "Business", "Education", "Counseling", "Criminal Justice", "Healthcare", "Information and Technology",
    "Art and Design", "Cosmetology", "Automotive", "Culinary", "Fashion", "Truck Driving", "Fire Science",
    "Writing", "Liberal Arts", "History", "Religious Studies", "Communications", "Math and Science",
    "Environmental Science", "Not Listed", "Not-Edu", "Highschool", "Social Work", "Trade and Vocational",
    "Nursing", "Agriculture/Horticulture", "Aviation", "Barbering", "Carpentry", "Childcare", "HVAC",
    "Electrician", "Interior Design", "Landscaping", "Massage Therapy", "Welding", "Biological Science",
    "Veterinarian", "Engineering", "Animal Science", "Mortuary Science",
];

export const EDU_LEVELS = [
    {id: 1, label: "GED"},
    {id: 2, label: "Still in High School"},
    {id: 3, label: "High School"},
    {id: 4, label: "Some College"},
    {id: 5, label: "Associates"},
    {id: 6, label: "Bachelors"},
    {id: 7, label: "Masters"},
    {id: 8, label: "Doctoral"},
    {id: 9, label: "Other"},
];

export const DEGREE_LEVELS = [
    {id: 5, label: "Associates"},
    {id: 6, label: "Bachelors"},
    {id: 7, label: "Masters"},
    {id: 8, label: "Doctoral"},
    {id: 9, label: "Other"},
];

export const WHEN_STARTS = [
    {id: "0months", label: "Immediately"},
    {id: "1months", label: "1-3 Months"},
    {id: "3months", label: "3-6 Months"},
    {id: "6months", label: "6+ Months"},
];

export function newClickFeedCampaignDefaults() {
    return {
        hp_campaign_id: "",
        hp_campaign_id_pr: "",
        url: "https://",
        dailyCap: 0,
        monthlyCap: 0,
        defaultRpm: 500,
        active: true,
        zips: "",
        zipsAllowed: true,
        publishersAllowed: false,
        militaryOnly: false,
        blockAlreadyEnrolled: false,
        zeroDailyEom: false,
        hsMin: null,
        hsMax: null,
        categories: [] as string[],
        whenStarts: ["0months", "1months", "3months", "6months"],
        trafficTypesBlocked: [] as string[],
        programIds: [] as number[],
        publisherIds: [] as number[],
        eduLevels: [1, 2, 3, 4, 5, 6, 7, 8, 9],
        degreeLevels: [5, 6, 7, 8, 9],
        managed: false,
    };
}
